<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import type { Partner, Task } from '@stuut/api/models';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TaskPriority from '@/components/common/TaskPriority.vue';
  import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';
  import TaskStatusBadge from '@/components/tasks/TaskStatusBadge.vue';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    disabled?: boolean;
    editMode?: boolean;
    partner?: Partner;
    task?: Task;
  }

  const props = defineProps<Props>();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);

  const componentTask = computed(() => props.task ?? currentTask.value);
</script>

<template>
  <div class="space-y-2">
    <div class="flex flex-nowrap items-center w-full justify-between gap-x-5">
      <div
        class="flex flex-nowrap items-center gap-x-2.5 w-full truncate"
        :class="{
          'opacity-40': !componentTask?.isOpen && !editMode,
        }"
      >
        <Tooltip :delay-duration="0">
          <TooltipTrigger class="truncate">
            <span class="text-h3 text-left text-nowrap truncate">
              <slot v-if="$slots.title" name="title" />
            </span>
          </TooltipTrigger>

          <TooltipContent>
            <template v-if="$slots.tooltip_content">
              <slot v-if="$slots.tooltip_content" name="tooltip_content" />
            </template>

            <template v-else>
              {{ componentTask?.label }}
            </template>
          </TooltipContent>
        </Tooltip>

        <TasksAssigneeSelect
          v-if="componentTask?.id"
          class="w-fit font-semibold [&>div]:w-fit!"
          :task="componentTask"
          :disabled="disabled"
          :ghost-button-variant="true"
          :hide-arrow-button="true"
          :show-avatar-only="true"
        />

        <TaskPriority
          v-if="componentTask?.id"
          :task="componentTask"
          size="xs"
          variant="indicator"
          :show-suffix="true"
          :urgent-always-badge="true"
        />
      </div>

      <div v-if="componentTask?.id" class="flex items-center justify-end gap-x-4 min-w-[200px]">
        <Timestamp
          v-if="componentTask?.created_at"
          class="text-muted-foreground! text-nowrap"
          :open-delay="500"
          :with-conversion-table="true"
          :time="componentTask?.created_at"
          local
          :show-from-now="true"
          format="M/D/YYYY h:mma"
        />

        <TaskStatusBadge v-if="componentTask" :task="componentTask" badge-size="xs" />
      </div>
    </div>
  </div>
</template>

<style></style>
